import React, { createContext, useCallback, useContext, useMemo } from 'react'

import useAuth from 'hooks/use-auth'
import { useGetExternalIntegrationsStatusChecks } from 'queries/external-integrations/use-get-external-integrations-status-checks'
import { externalIntegrationsStatusChecksGet } from 'utils/endpoints-access'
import { ExternalIntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  completeTeamExternalIntegrations,
  convertResponseToTeamExternalIntegration,
  removeSavedTeamExternalIntegrations
} from 'utils/team-external-integrations/team-external-integrations-util'

import type { AxiosError } from 'axios'
import type { QueryObserverResult } from 'react-query'
import type {
  TeamExternalIntegration,
  TeamExternalIntegrationResponse
} from 'types/api/status-checks/status-check'

export const ERROR_MESSAGES_EXTERNAL_INTEGRATIONS = {
  unknownFailure:
    'Failed to fetch External Integrations. Please try again later.'
}

export interface StatusCheckExternalIntegrationsContextValues {
  evaluatedTeamId: string
  externalIntegrations: TeamExternalIntegrationResponse[] | undefined
  externalIntegrationsError: AxiosError | null
  hasErrorExternalIntegrations: boolean
  isFetchingExternalIntegrations: boolean
  isLoadingExternalIntegrations: boolean
  getAvailableExternalIntegrations: (
    removeSavedIntegrations: boolean
  ) => TeamExternalIntegration[]
  reFetchExternalIntegrations(): Promise<
    QueryObserverResult<TeamExternalIntegrationResponse[], AxiosError>
  >
  teamExternalIntegrations: TeamExternalIntegration[]
}

export const StatusCheckExternalIntegrationsContext = createContext<
  StatusCheckExternalIntegrationsContextValues | undefined
>(undefined)

export const StatusCheckExternalIntegrationsProvider: React.FC<{
  teamId?: string
}> = ({ teamId, children }) => {
  const { currentTeamId, hasEndpointAccess } = useAuth()
  const evaluatedTeamId = teamId ?? currentTeamId
  const canReadStatusChecks = hasEndpointAccess(
    externalIntegrationsStatusChecksGet,
    { teamId }
  )

  const {
    error: externalIntegrationsError,
    data: externalIntegrations,
    isError: hasErrorExternalIntegrations,
    isFetching: isFetchingExternalIntegrations,
    isLoading: isLoadingExternalIntegrations,
    refetch: reFetchExternalIntegrations
  } = useGetExternalIntegrationsStatusChecks(evaluatedTeamId, {
    /*
     * TODO EN-7894 for backwards compatibility, older pages are not handling when priv is not met.
     * In those cases, allow network call to be made and BE will handle the priv check
     */
    enabled: teamId ? canReadStatusChecks : true
  })

  const savedTeamExternalIntegrations =
    convertResponseToTeamExternalIntegration(externalIntegrations)

  const getAvailableExternalIntegrations = useCallback(
    (removeSavedIntegrations) => {
      return removeSavedIntegrations
        ? removeSavedTeamExternalIntegrations(
            completeTeamExternalIntegrations(
              ExternalIntegrationType.StatusCheck,
              [],
              true
            ),
            savedTeamExternalIntegrations
          )
        : completeTeamExternalIntegrations(
            ExternalIntegrationType.StatusCheck,
            savedTeamExternalIntegrations,
            true
          )
    },
    [savedTeamExternalIntegrations]
  )

  const values = useMemo(() => {
    return {
      evaluatedTeamId,
      externalIntegrations: externalIntegrations ?? [],
      externalIntegrationsError,
      hasErrorExternalIntegrations,
      isFetchingExternalIntegrations,
      isLoadingExternalIntegrations,
      getAvailableExternalIntegrations,
      reFetchExternalIntegrations,
      teamExternalIntegrations: savedTeamExternalIntegrations
    }
  }, [
    evaluatedTeamId,
    externalIntegrations,
    externalIntegrationsError,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    isLoadingExternalIntegrations,
    getAvailableExternalIntegrations,
    reFetchExternalIntegrations,
    savedTeamExternalIntegrations
  ])

  return (
    <StatusCheckExternalIntegrationsContext.Provider value={values}>
      {children}
    </StatusCheckExternalIntegrationsContext.Provider>
  )
}

export function useStatusCheckExternalIntegrationsContext() {
  const context = useContext(StatusCheckExternalIntegrationsContext)

  if (!context) {
    throw new Error(
      'useStatusCheckExternalIntegrationsContext must be used within its respective provider'
    )
  }

  return context
}
